import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import AppShell from '../components/layout/AppShell';
import { Printer, Download, Phone, Shield, Heart, MapPin, Users, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const EmergencyContactReportPage = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { user, organization } = useAuth();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchChildren = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/children?active_only=true`);
      const sorted = (res.data || []).sort((a, b) => a.name.localeCompare(b.name));
      setChildren(sorted);
    } catch {
      toast.error('Failed to load children');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchChildren(); }, [fetchChildren]);

  const handlePrint = () => {
    window.print();
  };

  const formatPhone = (phone) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) {
      return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
    }
    return phone;
  };

  if (loading) {
    const spinner = (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
    if (embedded) return spinner;
    return (
      <AppShell pageTitle="Emergency Contact Report" breadcrumb={true}>
        {spinner}
      </AppShell>
    );
  }

  if (embedded) {
    return (
      <div className="bg-white p-4" data-testid="emergency-contact-report-embedded">
        {/* Embedded header */}
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
          <div>
            {organization && (
              <div className="mb-1">
                <span className="text-sm font-bold text-blue-800">{organization.name}</span>
                {organization.address && <span className="text-xs text-gray-500 ml-2">{organization.address}</span>}
              </div>
            )}
            <h3 className="text-base font-semibold text-gray-900">Emergency Contact Report</h3>
            <p className="text-xs text-gray-500">{children.length} active children</p>
          </div>
          <Button
            onClick={handlePrint}
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="print-report-btn-embedded"
          >
            <Printer className="w-4 h-4 mr-1" />
            Print Report
          </Button>
        </div>
        {/* Children cards */}
        <div className="space-y-3">
          {children.map((child) => {
            const authorizedPersons = (child.pickupPersons || []).filter(p => p.authorizedForRelease);
            const emergencyContacts = child.emergencyContacts || [];
            const insurance = child.healthInsurance || {};
            const category = (child.category || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            return (
              <div key={child.id} className="border border-gray-300 rounded-lg overflow-hidden" data-testid={`child-card-embedded-${child.id}`}>
                <div className="bg-blue-700 text-white px-4 py-2 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-sm">{child.name}</span>
                    <span className="text-blue-200 text-xs">({category})</span>
                  </div>
                  {child.birthdate && (
                    <span className="text-blue-200 text-xs">DOB: {new Date(child.birthdate + 'T00:00:00').toLocaleDateString()}</span>
                  )}
                </div>
                <div className="p-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <div>
                      <h4 className="text-xs font-bold text-blue-800 uppercase tracking-wide border-b border-blue-200 pb-1 mb-2 flex items-center gap-1">
                        <Users className="w-3 h-3" /> Parent / Guardian Contacts
                      </h4>
                      {(child.pickupPersons || []).length > 0 ? (
                        <table className="w-full text-xs">
                          <thead><tr className="text-left text-gray-500"><th className="pb-1 font-medium">Name</th><th className="pb-1 font-medium">Relationship</th><th className="pb-1 font-medium">Phone</th></tr></thead>
                          <tbody>
                            {child.pickupPersons.map((p, i) => (
                              <tr key={i} className="border-t border-gray-100">
                                <td className="py-1 font-medium text-gray-900">{p.name}</td>
                                <td className="py-1 text-gray-600">{p.relationship || '-'}</td>
                                <td className="py-1 text-gray-900">{formatPhone(p.phone)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : <p className="text-xs text-gray-400 italic">No contacts on file</p>}
                    </div>
                    {authorizedPersons.length > 0 && (
                      <div>
                        <h4 className="text-xs font-bold text-green-800 uppercase tracking-wide border-b border-green-200 pb-1 mb-2 flex items-center gap-1">
                          <Shield className="w-3 h-3" /> Authorized for Release
                        </h4>
                        <table className="w-full text-xs">
                          <thead><tr className="text-left text-gray-500"><th className="pb-1 font-medium">Name</th><th className="pb-1 font-medium">Phone</th><th className="pb-1 font-medium">Address</th></tr></thead>
                          <tbody>
                            {authorizedPersons.map((p, i) => (
                              <tr key={i} className="border-t border-gray-100">
                                <td className="py-1 font-medium text-gray-900">{p.name}</td>
                                <td className="py-1 text-gray-900">{formatPhone(p.phone)}</td>
                                <td className="py-1 text-gray-600">{p.address || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {(child.specialInstructions || child.pickupPersons?.some(p => p.restrictions)) && (
                      <div>
                        <h4 className="text-xs font-bold text-red-800 uppercase tracking-wide border-b border-red-200 pb-1 mb-1">
                          Restrictions / Special Instructions
                        </h4>
                        {child.specialInstructions && <p className="text-xs text-red-700 font-medium">{child.specialInstructions}</p>}
                        {child.pickupPersons?.filter(p => p.restrictions).map((p, i) => (
                          <p key={i} className="text-xs text-gray-700">{p.name}: {p.restrictions}</p>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <div>
                      <h4 className="text-xs font-bold text-orange-800 uppercase tracking-wide border-b border-orange-200 pb-1 mb-2 flex items-center gap-1">
                        <Phone className="w-3 h-3" /> Emergency Contacts (Call Only)
                      </h4>
                      {emergencyContacts.length > 0 ? (
                        <table className="w-full text-xs">
                          <thead><tr className="text-left text-gray-500"><th className="pb-1 font-medium">Name</th><th className="pb-1 font-medium">Phone</th></tr></thead>
                          <tbody>
                            {emergencyContacts.map((c, i) => (
                              <tr key={i} className="border-t border-gray-100">
                                <td className="py-1 font-medium text-gray-900">{c.name}</td>
                                <td className="py-1 text-gray-900">{formatPhone(c.phone)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : <p className="text-xs text-gray-400 italic">No emergency contacts on file</p>}
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-purple-800 uppercase tracking-wide border-b border-purple-200 pb-1 mb-2 flex items-center gap-1">
                        <Heart className="w-3 h-3" /> Health Insurance
                      </h4>
                      {insurance.provider ? (
                        <div className="text-xs space-y-0.5">
                          <div className="flex gap-2"><span className="text-gray-500 font-medium w-20">Provider:</span><span className="text-gray-900">{insurance.provider}</span></div>
                          <div className="flex gap-2"><span className="text-gray-500 font-medium w-20">Policy #:</span><span className="text-gray-900">{insurance.policyNumber || '-'}</span></div>
                          {insurance.groupNumber && <div className="flex gap-2"><span className="text-gray-500 font-medium w-20">Group #:</span><span className="text-gray-900">{insurance.groupNumber}</span></div>}
                          <div className="flex gap-2"><span className="text-gray-500 font-medium w-20">Subscriber:</span><span className="text-gray-900">{insurance.subscriberName || '-'}</span></div>
                        </div>
                      ) : <p className="text-xs text-gray-400 italic">No insurance on file</p>}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <AppShell pageTitle="Emergency Contact Report" breadcrumb={true}>
    <div className="max-w-full bg-white min-h-screen -m-4 p-6 pl-8" data-testid="emergency-contact-report">
      {/* Screen header - hidden on print */}
      <div className="print:!hidden mb-6">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 mb-4 px-3 py-2 rounded-lg text-sm font-medium text-ink-muted hover:text-brand-primary hover:bg-brand-primary/5 transition-colors"
          data-testid="emergency-contact-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        {/* Organization Info */}
        {organization && (
          <div className="mb-4 text-center">
            <h1 className="text-xl font-bold text-blue-800">{organization.name}</h1>
            {organization.address && (
              <p className="text-sm text-gray-600">{organization.address}</p>
            )}
            {organization.phone && (
              <p className="text-sm text-gray-600">{organization.phone}</p>
            )}
          </div>
        )}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Emergency Contact Report</h2>
            <p className="text-sm text-gray-500 mt-1">{children.length} active children</p>
          </div>
          <Button
            onClick={handlePrint}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="print-report-btn"
          >
            <Printer className="w-4 h-4 mr-2" />
            Print Report
          </Button>
        </div>
      </div>

      {/* Print-only header */}
      <div className="hidden print:!block mb-4">
        <div style={{ display: 'flex', alignItems: 'center', borderBottom: '2px solid #1e40af', paddingBottom: '8px', marginBottom: '12px' }}>
          <img src="/simple-sign-in-logo.png" alt="Simple Sign-In" style={{ height: '40px', marginRight: '12px', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            {organization && (
              <>
                <h1 style={{ fontSize: '14pt', fontWeight: 'bold', color: '#1e40af', margin: 0 }}>
                  {organization.name}
                </h1>
                {organization.address && (
                  <p style={{ fontSize: '9pt', color: '#444', margin: '2px 0 0 0' }}>{organization.address}</p>
                )}
                {organization.phone && (
                  <p style={{ fontSize: '9pt', color: '#444', margin: '2px 0 0 0' }}>{organization.phone}</p>
                )}
              </>
            )}
            <h2 style={{ fontSize: '12pt', fontWeight: 'bold', color: '#1e40af', margin: '4px 0 0 0' }}>
              Emergency Contact Report
            </h2>
            <p style={{ fontSize: '8pt', color: '#666', margin: '2px 0 0 0' }}>
              Printed: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} | {children.length} Children
            </p>
          </div>
        </div>
      </div>

      {/* Children cards */}
      <div className="space-y-4 print:space-y-2">
        {children.map((child) => {
          const authorizedPersons = (child.pickupPersons || []).filter(p => p.authorizedForRelease);
          const emergencyContacts = child.emergencyContacts || [];
          const insurance = child.healthInsurance || {};
          const category = (child.category || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

          return (
            <div
              key={child.id}
              className="border border-gray-300 rounded-lg overflow-hidden print:break-inside-avoid print:rounded-none print:border-gray-400"
              data-testid={`child-card-${child.id}`}
            >
              {/* Child Name Header */}
              <div className="bg-blue-700 text-white px-4 py-2 print:py-1 print:bg-gray-100 print:text-black flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="font-bold text-lg print:text-sm">{child.name}</span>
                  <span className="text-blue-200 text-sm print:text-xs">({category})</span>
                </div>
                {child.birthdate && (
                  <span className="text-blue-200 text-sm print:text-xs">
                    DOB: {new Date(child.birthdate + 'T00:00:00').toLocaleDateString()}
                  </span>
                )}
              </div>

              <div className="p-4 print:p-2 grid grid-cols-1 lg:grid-cols-2 print:grid-cols-2 gap-4 print:gap-2">
                {/* Left Column: Pickup Persons & Authorized for Release */}
                <div className="space-y-3 print:space-y-1">
                  {/* Pickup / Guardian Persons */}
                  <div>
                    <h4 className="text-xs font-bold text-blue-800 uppercase tracking-wide border-b border-blue-200 pb-1 mb-2 print:mb-1 flex items-center gap-1">
                      <Users className="w-3 h-3" /> Parent / Guardian Contacts
                    </h4>
                    {(child.pickupPersons || []).length > 0 ? (
                      <table className="w-full text-sm print:text-xs">
                        <thead>
                          <tr className="text-left text-gray-500">
                            <th className="pb-1 font-medium">Name</th>
                            <th className="pb-1 font-medium">Relationship</th>
                            <th className="pb-1 font-medium">Phone</th>
                          </tr>
                        </thead>
                        <tbody>
                          {child.pickupPersons.map((p, i) => (
                            <tr key={i} className="border-t border-gray-100">
                              <td className="py-1 font-medium text-gray-900">{p.name}</td>
                              <td className="py-1 text-gray-600">{p.relationship || '-'}</td>
                              <td className="py-1 text-gray-900">{formatPhone(p.phone)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="text-xs text-gray-400 italic">No contacts on file</p>
                    )}
                  </div>

                  {/* Persons Authorized for Release */}
                  {authorizedPersons.length > 0 && (
                    <div>
                      <h4 className="text-xs font-bold text-green-800 uppercase tracking-wide border-b border-green-200 pb-1 mb-2 print:mb-1 flex items-center gap-1">
                        <Shield className="w-3 h-3" /> Authorized for Release
                      </h4>
                      <table className="w-full text-sm print:text-xs">
                        <thead>
                          <tr className="text-left text-gray-500">
                            <th className="pb-1 font-medium">Name</th>
                            <th className="pb-1 font-medium">Phone</th>
                            <th className="pb-1 font-medium">Address</th>
                          </tr>
                        </thead>
                        <tbody>
                          {authorizedPersons.map((p, i) => (
                            <tr key={i} className="border-t border-gray-100">
                              <td className="py-1 font-medium text-gray-900">{p.name}</td>
                              <td className="py-1 text-gray-900">{formatPhone(p.phone)}</td>
                              <td className="py-1 text-gray-600">{p.address || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Restrictions / Special Instructions */}
                  {(child.specialInstructions || child.pickupPersons?.some(p => p.restrictions)) && (
                    <div>
                      <h4 className="text-xs font-bold text-red-800 uppercase tracking-wide border-b border-red-200 pb-1 mb-1">
                        Restrictions / Special Instructions
                      </h4>
                      {child.specialInstructions && (
                        <p className="text-xs text-red-700 font-medium">{child.specialInstructions}</p>
                      )}
                      {child.pickupPersons?.filter(p => p.restrictions).map((p, i) => (
                        <p key={i} className="text-xs text-gray-700">{p.name}: {p.restrictions}</p>
                      ))}
                    </div>
                  )}
                </div>

                {/* Right Column: Emergency Contacts & Insurance */}
                <div className="space-y-3 print:space-y-1">
                  {/* Emergency Contacts (Call Only) */}
                  <div>
                    <h4 className="text-xs font-bold text-orange-800 uppercase tracking-wide border-b border-orange-200 pb-1 mb-2 print:mb-1 flex items-center gap-1">
                      <Phone className="w-3 h-3" /> Emergency Contacts (Call Only)
                    </h4>
                    {emergencyContacts.length > 0 ? (
                      <table className="w-full text-sm print:text-xs">
                        <thead>
                          <tr className="text-left text-gray-500">
                            <th className="pb-1 font-medium">Name</th>
                            <th className="pb-1 font-medium">Phone</th>
                          </tr>
                        </thead>
                        <tbody>
                          {emergencyContacts.map((c, i) => (
                            <tr key={i} className="border-t border-gray-100">
                              <td className="py-1 font-medium text-gray-900">{c.name}</td>
                              <td className="py-1 text-gray-900">{formatPhone(c.phone)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="text-xs text-gray-400 italic">No emergency contacts on file</p>
                    )}
                  </div>

                  {/* Health Insurance */}
                  <div>
                    <h4 className="text-xs font-bold text-purple-800 uppercase tracking-wide border-b border-purple-200 pb-1 mb-2 print:mb-1 flex items-center gap-1">
                      <Heart className="w-3 h-3" /> Health Insurance
                    </h4>
                    {insurance.provider ? (
                      <div className="text-sm print:text-xs space-y-0.5">
                        <div className="flex gap-2">
                          <span className="text-gray-500 font-medium w-24">Provider:</span>
                          <span className="text-gray-900">{insurance.provider}</span>
                        </div>
                        <div className="flex gap-2">
                          <span className="text-gray-500 font-medium w-24">Policy #:</span>
                          <span className="text-gray-900">{insurance.policyNumber || '-'}</span>
                        </div>
                        {insurance.groupNumber && (
                          <div className="flex gap-2">
                            <span className="text-gray-500 font-medium w-24">Group #:</span>
                            <span className="text-gray-900">{insurance.groupNumber}</span>
                          </div>
                        )}
                        <div className="flex gap-2">
                          <span className="text-gray-500 font-medium w-24">Subscriber:</span>
                          <span className="text-gray-900">{insurance.subscriberName || '-'}</span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 italic">No insurance on file</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
    </AppShell>
  );
};

export default EmergencyContactReportPage;
